import { useState, useMemo, useRef } from 'react';
import { Check, CheckCircle, Copy, Download, Edit2, Eye, Link2, Plus, Trash2, Upload, Users } from 'lucide-react';
import { Modal, confirmDialog } from './ui.jsx';
import { POLICY_TYPES, getPolicyType } from '../policyTypes.js';
import { CARRIERS_BY_TYPE } from './PoliciesView.jsx';
import { toast } from './Toast.jsx';
import { uploadFileToBlob, freshResourceFileUrl } from '../api.js';
import { revisarArchivoDeVideo, textoDelAviso } from '../videoCompatible.js';
import { copyText } from '../utils.js';
import VisorDeVideo from './VisorDeVideo.jsx';

const isBlobFileUrl = (u) => /blob\.core\.windows\.net/.test(String(u || ''));

const RESOURCE_CATEGORIES = ["Training","Enrollment Guides","Plan Benefits","Compliance","Marketing Materials","Client Education","Forms","Other"];

// Today as 'YYYY-MM-DD' in LOCAL time. toISOString().split('T')[0] is UTC, so
// uploads/tokens created after ~8pm Florida (UTC-4) were stamped tomorrow.
const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function ResourcesView({ resources, onResourcesChange, resourceTokens, onResourceTokensChange, onSaveResource, onDeleteResource, onCreateResourceTokens, clients, policies, currentUser, t, lang, canEdit = true }) {
  // Pide al SERVIDOR el enlace del portal de cada cliente y devuelve lo que
  // conteste. Antes esta pantalla se inventaba el token
  // (`portal_<idDelCliente>_<8 de Math.random()>`) y el backend lo guardaba tal
  // cual: llevaba el id del cliente dentro de la URL y su parte "aleatoria"
  // salia de un generador predecible (auditoria 2026-08-23, E-118).
  //
  // El servidor es idempotente por cliente: si el enlace sigue vivo devuelve
  // ese mismo, y si vencio o fue revocado acuna uno nuevo. Por eso aqui ya no
  // se busca nada en local — se pregunta y se usa la respuesta.
  const pedirTokens = async (clientIds) => {
    const ids = (clientIds || []).filter(Boolean);
    if (!ids.length) return [];
    if (onCreateResourceTokens) {
      const saved = await onCreateResourceTokens(ids.map(clientId => ({ clientId, createdBy: currentUser?.id })));
      return Array.isArray(saved) ? saved : [];
    }
    // Sin backend (modo local): al menos que el token sea aleatorio de verdad
    // y no lleve el id del cliente escrito dentro.
    const aleatorio = () => {
      const b = new Uint8Array(24);
      window.crypto.getRandomValues(b);
      return btoa(String.fromCharCode(...b)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    };
    const locales = ids.map(clientId => {
      const existing = (resourceTokens || []).find(t => t.clientId === clientId);
      return existing || { token: aleatorio(), clientId, createdAt: todayLocal(), createdBy: currentUser?.id };
    });
    onResourceTokensChange(prev => {
      const known = new Set(prev.map(t => t.token));
      return [...prev, ...locales.filter(t => !known.has(t.token))];
    });
    return locales;
  };
  const tr = t?.resources || {};
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterCarrier, setFilterCarrier] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [sortBy, setSortBy] = useState("recent"); // recent | category | title | carrier
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showPortalModal, setShowPortalModal] = useState(false);
  const [portalClientId, setPortalClientId] = useState("");
  const [portalCarrierFilter, setPortalCarrierFilter] = useState("");
  const [portalSelectedClients, setPortalSelectedClients] = useState([]);
  const [portalGenerated, setPortalGenerated] = useState([]); // [{clientId, name, link, token, expiresAt}]
  const [portalBusy, setPortalBusy] = useState(false);        // pidiendo enlaces al servidor
  const [revocados, setRevocados] = useState([]);             // tokens matados en esta sesion
  const [copiedLink, setCopiedLink] = useState(null);
  // Per-resource share link (separate from the per-client portal link above).
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const [linkBusyId, setLinkBusyId] = useState(null);
  const [previewRes, setPreviewRes] = useState(null);
  // Open the preview with a URL signed seconds ago rather than the one the
  // list was loaded with (E-085). Every <video>/<img>/<iframe> in the modal
  // reads previewRes.fileUrl, so swapping it here covers all of them at once.
  const openPreview = async (res) => {
    setPreviewRes({ ...res, fileUrl: (await freshResourceFileUrl(res)) || res.fileUrl });
  };
  const fileInputRef = useRef(null);
  const videoFileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  // Upload a video file straight to Azure Blob (no 5MB cap) and store its URL.
  const handleVideoFile = async (file) => {
    if (!file) return;
    if (typeof window === "undefined" || !window.__deskteAuth) { toast.warn(lang === "es" ? "Conéctate para subir archivos." : "Sign in to upload files."); return; }
    setUploading(true); setUploadPct(0);
    try {
      const url = await uploadFileToBlob(file, (p) => setUploadPct(p));
      setForm(f => ({ ...f, fileUrl: url, fileName: file.name, fileSize: file.size, type: "video" }));
      toast.success(lang === "es" ? "Video subido" : "Video uploaded");
    } catch (e) { toast.error((lang === "es" ? "No se pudo subir: " : "Upload failed: ") + (e.message || e)); }
    finally { setUploading(false); }
  };

  // Form state
  const emptyForm = { title:"", description:"", type:"pdf", policyType:"", carrier:"", category:"", fileData:"", fileUrl:"", fileName:"", fileSize:0, sharedWithClients:[], sharedWithAll:false, tags:"" };
  const [form, setForm] = useState(emptyForm);

  // Carriers in the dropdown: union of (a) the agency's actual policies +
  // resources (so anything they've actually used shows up) AND (b) the
  // default catalog from PoliciesView (so non-Health agencies get Life /
  // P&C / Auto / Home etc. without having to type them by hand first).
  const allCarriers = useMemo(() => {
    const fromPolicies = (policies||[]).map(p=>p.carrier).filter(Boolean);
    const fromResources = (resources||[]).map(r=>r.carrier).filter(Boolean);
    const fromCatalog = Object.values(CARRIERS_BY_TYPE).flat();
    return [...new Set([...fromPolicies,...fromResources,...fromCatalog])].sort();
  }, [policies, resources]);

  // Map: policyType → carriers. Merges the default catalog with carriers
  // the agency has already used for that type so previously-typed
  // carriers always survive at the top of the list.
  const carriersByPolicyType = useMemo(() => {
    const map = {};
    Object.entries(CARRIERS_BY_TYPE).forEach(([type, list]) => {
      map[type] = new Set(list);
    });
    (policies||[]).forEach(p => {
      if (!p.type || !p.carrier) return;
      if (!map[p.type]) map[p.type] = new Set();
      map[p.type].add(p.carrier);
    });
    Object.keys(map).forEach(k => { map[k] = [...map[k]].sort(); });
    return map;
  }, [policies]);

  // Carriers filtered by selected policy type (or all if none selected)
  const filteredCarriers = useMemo(() => {
    if (!form.policyType) return allCarriers;
    return carriersByPolicyType[form.policyType] || allCarriers;
  }, [form.policyType, allCarriers, carriersByPolicyType]);

  const allCategories = RESOURCE_CATEGORIES;

  // Filter + sort resources
  const filtered = useMemo(() => {
    const rows = (resources||[]).filter(r => {
      // Team-training materials live in their own dedicated section, not here.
      if (r.category === "Team Training") return false;
      if (filterType !== "all" && r.type !== filterType) return false;
      if (filterCarrier !== "all" && r.carrier !== filterCarrier) return false;
      if (filterCategory !== "all" && r.category !== filterCategory) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!(r.title||"").toLowerCase().includes(s) && !(r.description||"").toLowerCase().includes(s) && !(r.carrier||"").toLowerCase().includes(s) && !(r.tags||[]).join(" ").toLowerCase().includes(s)) return false;
      }
      return true;
    });
    const byTitle = (a, b) => (a.title||"").localeCompare(b.title||"");
    if (sortBy === "category") rows.sort((a, b) => (a.category||"").localeCompare(b.category||"") || byTitle(a, b));
    else if (sortBy === "title") rows.sort(byTitle);
    else if (sortBy === "carrier") rows.sort((a, b) => (a.carrier||"").localeCompare(b.carrier||"") || byTitle(a, b));
    else rows.sort((a, b) => String(b.uploadedAt||"").localeCompare(String(a.uploadedAt||""))); // recent first
    return rows;
  }, [resources, filterType, filterCarrier, filterCategory, search, sortBy]);

  const typeIcon = (type) => ({ video:"🎬", pdf:"📕", image:"🖼️", document:"📄" }[type] || "📄");
  const typeColor = (type) => ({ video:"#8b5cf6", pdf:"#ef4444", image:"#3b82f6", document:"#f59e0b" }[type] || "#64748b");
  const fmtSize = (bytes) => { if(!bytes) return ""; if(bytes<1024) return bytes+" B"; if(bytes<1048576) return (bytes/1024).toFixed(1)+" KB"; return (bytes/1048576).toFixed(1)+" MB"; };

  // Files (PDF/image/document) now upload straight to Azure Blob — browser →
  // storage, same path videos use — instead of being stored as base64 in the DB.
  // That keeps the DB light and the /resources list fast as agencies grow.
  // Old records still carry fileData; the preview falls back to it (fileUrl || fileData).
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (typeof window === "undefined" || !window.__deskteAuth) { toast.warn(lang === "es" ? "Conéctate para subir archivos." : "Sign in to upload files."); return; }
    if (file.size > 100 * 1024 * 1024) { toast.warn(lang === "es" ? "Archivo muy grande (máx 100MB)" : "File too large (max 100MB)"); return; }
    setUploading(true); setUploadPct(0);
    try {
      const url = await uploadFileToBlob(file, (p) => setUploadPct(p));
      // Store the blob URL and clear any legacy base64 so we never keep both.
      setForm(f => ({ ...f, fileUrl: url, fileData: "", fileName: file.name, fileSize: file.size }));
      toast.success(lang === "es" ? "Archivo subido" : "File uploaded");
      // Mismo aviso que en Entrenamiento del Equipo, y por el mismo motivo: se
      // sube igual, pero quien lo sube se entera AHORA -con el telefono en la
      // mano- de que su equipo no va a poder verlo. (Ticket de Gabriel.)
      if ((file.type || '').startsWith('video/')) {
        const revision = await revisarArchivoDeVideo(file);
        if (revision.avisar) toast.warn(textoDelAviso(revision, lang === "es"), { duration: 15000 });
      }
    } catch (err) {
      toast.error((lang === "es" ? "No se pudo subir: " : "Upload failed: ") + (err.message || err));
    } finally { setUploading(false); }
  };

  const handleSave = () => {
    // Carrier is OPTIONAL — team-training resources (videos/docs for the team)
    // aren't tied to an insurance carrier. Only title + category are required.
    if (!form.title.trim() || !form.category) return;
    if (form.type === "video" && !form.fileUrl.trim()) return;
    if (form.type !== "video" && !form.fileData && !form.fileUrl && !editingId) return;

    const tagsArr = (form.tags||"").split(",").map(t=>t.trim()).filter(Boolean);

    if (editingId) {
      const existing = (resources||[]).find(r => r.id === editingId) || {};
      // Use form.fileData directly (handleEdit seeds it from the existing record;
      // a re-upload to blob clears it to ""). Falling back to existing.fileData
      // here would re-attach the old base64 alongside the new fileUrl and keep
      // bloating the DB — the very thing this change removes.
      const updated = { ...existing, id:editingId, title:form.title.trim(), description:form.description.trim(), type:form.type, policyType:form.policyType, carrier:form.carrier.trim(), category:form.category, fileData:form.fileData, fileUrl:form.fileUrl, fileName:form.fileName||existing.fileName, fileSize:form.fileSize||existing.fileSize, sharedWithClients:form.sharedWithClients, sharedWithAll:form.sharedWithAll, tags:tagsArr };
      if (onSaveResource) onSaveResource(updated);
      else onResourcesChange(prev => prev.map(r => r.id === editingId ? updated : r));
    } else {
      const newRes = { id:"res_"+Date.now(), title:form.title.trim(), description:form.description.trim(), type:form.type, policyType:form.policyType, carrier:form.carrier.trim(), category:form.category, fileData:form.fileData, fileUrl:form.fileUrl, fileName:form.fileName, fileSize:form.fileSize, uploadedBy:currentUser?.id, uploadedAt:todayLocal(), sharedWithClients:form.sharedWithClients, sharedWithAll:form.sharedWithAll, tags:tagsArr };
      if (onSaveResource) onSaveResource(newRes);
      else onResourcesChange(prev => [...prev, newRes]);
    }
    setShowForm(false); setEditingId(null); setForm(emptyForm);
  };

  const handleEdit = (res) => {
    setForm({ title:res.title, description:res.description||"", type:res.type, policyType:res.policyType||"", carrier:res.carrier, category:res.category, fileData:res.fileData||"", fileUrl:res.fileUrl||"", fileName:res.fileName||"", fileSize:res.fileSize||0, sharedWithClients:res.sharedWithClients||[], sharedWithAll:res.sharedWithAll||false, tags:(res.tags||[]).join(", ") });
    setEditingId(res.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if(!(await confirmDialog({ message: tr.deleteConfirm||"Delete this resource?", danger: true }))) return;
    if (onDeleteResource) onDeleteResource(id);
    else onResourcesChange(prev=>prev.filter(r=>r.id!==id));
  };

  // Clients filtered by carrier for portal modal
  const portalFilteredClients = useMemo(() => {
    if (!portalCarrierFilter) return clients || [];
    const clientIdsWithCarrier = new Set((policies||[]).filter(p=>p.carrier===portalCarrierFilter).map(p=>p.clientId));
    return (clients||[]).filter(c=>clientIdsWithCarrier.has(c.id));
  }, [portalCarrierFilter, clients, policies]);

  const togglePortalClient = (clientId) => {
    setPortalSelectedClients(prev => prev.includes(clientId) ? prev.filter(id=>id!==clientId) : [...prev, clientId]);
  };

  const selectAllPortalClients = () => {
    const allIds = portalFilteredClients.map(c=>c.id);
    const allSelected = allIds.every(id=>portalSelectedClients.includes(id));
    if (allSelected) setPortalSelectedClients(prev=>prev.filter(id=>!allIds.includes(id)));
    else setPortalSelectedClients(prev=>[...new Set([...prev,...allIds])]);
  };

  const generatePortalLinks = async () => {
    if (portalSelectedClients.length === 0) return;
    setPortalBusy(true);
    try {
      // Path-based public link (served unauthenticated by AuthGate → backend
      // /resource-portal/:token). The old "#resources/" hash sat behind the login
      // wall, so a client without a Deskte account saw a blank screen.
      const base = window.location.origin + "/resource-portal/";
      const saved = await pedirTokens(portalSelectedClients);
      const generated = saved.map(row => {
        const client = (clients||[]).find(c=>c.id===row.clientId);
        const name = client ? `${client.firstName} ${client.lastName}` : row.clientId;
        return { clientId: row.clientId, name, link: base+row.token, token: row.token, email: client?.email||"", expiresAt: row.expiresAt || null };
      });
      // Si el servidor no devolvio nada NO se pinta una pantalla de exito
      // vacia: el error ya salio en un aviso y aqui no hay enlace que copiar.
      if (generated.length) setPortalGenerated(generated);
    } finally {
      setPortalBusy(false);
    }
  };

  // Matar un enlace del portal ahora mismo. El caso que lo pide es concreto y
  // no espera: se acaba de mandar a la direccion equivocada. Se confirma
  // porque no se deshace — el cliente que lo tenga deja de entrar.
  const revocarPortal = async (g) => {
    const ok = window.confirm(lang==="es"
      ? `¿Anular el enlace de ${g.name}?\n\nDejará de abrir al instante. Puedes generarle uno nuevo cuando quieras.`
      : `Revoke ${g.name}'s link?\n\nIt stops opening immediately. You can generate a new one any time.`);
    if (!ok) return;
    try {
      const { revokeResourceToken } = await import("../api.js");
      await revokeResourceToken(g.token);
      setRevocados(prev => [...prev, g.token]);
    } catch (err) {
      toast.error((lang==="es"?"No se pudo anular el enlace: ":"Could not revoke the link: ") + (err.message||err));
    }
  };

  // "Vence el 22/09/2026". Se dice en la propia lista porque el agente esta a
  // punto de pegar esto en un correo: un enlace con fecha se manda distinto a
  // uno que se cree eterno.
  const vigenciaTexto = (expiresAt) => {
    if (!expiresAt) return "";
    const d = new Date(expiresAt);
    if (isNaN(d.getTime())) return "";
    return (lang==="es" ? "Vence el " : "Expires ") + d.toLocaleDateString("en-US");
  };

  const copyAllPortalLinks = () => {
    const text = portalGenerated.map(g=>`${g.name}: ${g.link}`).join("\n");
    copyText(text).then(()=>{ setCopiedLink("all"); setTimeout(()=>setCopiedLink(null),2500); });
  };

  const copySinglePortalLink = (link, id) => {
    copyText(link).then(()=>{ setCopiedLink(id); setTimeout(()=>setCopiedLink(null),1500); });
  };

  // Copy a sendable link to ONE resource (Lissette 2026-07-21: the portal link
  // shares a whole filtered list per client, but sending a single flyer over
  // WhatsApp needs a link to just that file). The token is minted server-side
  // (it's an HMAC), so this asks the API rather than building the URL locally.
  const copyResourceLink = async (res) => {
    setLinkBusyId(res.id);
    try {
      const { getResourceShareLink } = await import('../api.js');
      const { path } = await getResourceShareLink(res.id);
      await navigator.clipboard.writeText(window.location.origin + path);
      setCopiedLinkId(res.id);
      setTimeout(()=>setCopiedLinkId(null), 2000);
    } catch (err) {
      toast.error((lang==="es"?"No se pudo copiar el enlace: ":"Could not copy the link: ") + (err.message||err));
    } finally {
      setLinkBusyId(null);
    }
  };

  // Legacy single-client generate (kept for backward compat)
  const generatePortalLink = async () => {
    if (!portalClientId) return;
    const [row] = await pedirTokens([portalClientId]);
    if (!row) return; // el error ya se aviso; no se copia un enlace inventado
    const link = window.location.origin + "/resource-portal/" + row.token;
    await copyText(link);
    setCopiedLink(row.token); setTimeout(()=>setCopiedLink(null),2000);
    setShowPortalModal(false); setPortalClientId("");
  };

  const copyExistingPortalLink = async (clientId) => {
    const [row] = await pedirTokens([clientId]);
    if (!row) return;
    const link = window.location.origin + "/resource-portal/" + row.token;
    await copyText(link);
    setCopiedLink(clientId); setTimeout(()=>setCopiedLink(null),2000);
  };

  const toggleClientShare = (clientId) => {
    setForm(f => {
      const list = f.sharedWithClients || [];
      return { ...f, sharedWithClients: list.includes(clientId) ? list.filter(id=>id!==clientId) : [...list, clientId] };
    });
  };

  const getYouTubeId = (url) => { const m = (url||"").match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]+)/); return m?m[1]:null; };
  const getVimeoId = (url) => { const m = (url||"").match(/vimeo\.com\/(\d+)/); return m?m[1]:null; };

  // Shared clients count display
  const sharedCount = (res) => {
    if (res.sharedWithAll) return tr.allClients || "All Clients";
    const n = (res.sharedWithClients||[]).length;
    return n + " " + (tr.clients || "clients");
  };

  return (
    <div style={{ padding:"1rem 1.5rem", maxWidth:1100, margin:"0 auto" }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"1.25rem", flexWrap:"wrap", gap:".5rem" }}>
        <div style={{ display:"flex", alignItems:"center", gap:".5rem" }}>
          <span style={{ fontSize:"1.5rem" }}>📚</span>
          <h2 style={{ fontSize:"1.2rem", fontWeight:800, color:"var(--c-white,#eef6ff)", margin:0 }}>{tr.title||"Resources"}</h2>
          <span style={{ background:"var(--c-cyan,#00d4ff)", color:"#000", fontSize:".65rem", fontWeight:800, padding:"2px 8px", borderRadius:20 }}>{(resources||[]).length}</span>
        </div>
        <div style={{ display:"flex", gap:".4rem", flexWrap:"wrap" }}>
          {canEdit && (
            <button onClick={()=>setShowPortalModal(true)} style={{ background:"var(--c-green,#00ff88)", color:"#000", border:"none", borderRadius:8, padding:".45rem .85rem", fontSize:".75rem", fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
              <Link2 size={13}/> {tr.generateLink||"Generate Portal Link"}
            </button>
          )}
          {canEdit && (
            <button onClick={()=>{setForm(emptyForm);setEditingId(null);setShowForm(true);}} style={{ background:"var(--c-cyan,#00d4ff)", color:"#000", border:"none", borderRadius:8, padding:".45rem .85rem", fontSize:".75rem", fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
              <Plus size={13}/> {tr.addResource||"Add Resource"}
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display:"flex", gap:".4rem", marginBottom:"1rem", flexWrap:"wrap", alignItems:"center" }}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={tr.search||"Search resources..."} style={{ flex:"1 1 180px", minWidth:150, padding:".45rem .75rem", borderRadius:8, border:"1px solid var(--c-border,#1a3a52)", background:"var(--c-card,#0d1f2d)", color:"var(--c-white,#eef6ff)", fontSize:".8rem" }}/>
        <select value={filterType} onChange={e=>setFilterType(e.target.value)} style={{ padding:".45rem .6rem", borderRadius:8, border:"1px solid var(--c-border,#1a3a52)", background:"var(--c-card,#0d1f2d)", color:"var(--c-white,#eef6ff)", fontSize:".75rem" }}>
          <option value="all">{tr.filterByType||"All Types"}</option>
          <option value="video">{tr.video||"Video"}</option>
          <option value="pdf">{tr.pdf||"PDF"}</option>
          <option value="image">{tr.image||"Image"}</option>
          <option value="document">{tr.document||"Document"}</option>
        </select>
        <select value={filterCarrier} onChange={e=>setFilterCarrier(e.target.value)} style={{ padding:".45rem .6rem", borderRadius:8, border:"1px solid var(--c-border,#1a3a52)", background:"var(--c-card,#0d1f2d)", color:"var(--c-white,#eef6ff)", fontSize:".75rem" }}>
          <option value="all">{tr.filterByCarrier||"All Carriers"}</option>
          {allCarriers.map(c=><option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterCategory} onChange={e=>setFilterCategory(e.target.value)} style={{ padding:".45rem .6rem", borderRadius:8, border:"1px solid var(--c-border,#1a3a52)", background:"var(--c-card,#0d1f2d)", color:"var(--c-white,#eef6ff)", fontSize:".75rem" }}>
          <option value="all">{tr.filterByCategory||"All Categories"}</option>
          {allCategories.map(c=><option key={c} value={c}>{c}</option>)}
        </select>
        <select value={sortBy} onChange={e=>setSortBy(e.target.value)} title={lang==="es"?"Ordenar por":"Sort by"} style={{ padding:".45rem .6rem", borderRadius:8, border:"1px solid var(--c-border,#1a3a52)", background:"var(--c-card,#0d1f2d)", color:"var(--c-white,#eef6ff)", fontSize:".75rem" }}>
          <option value="recent">{lang==="es"?"↕ Más recientes":"↕ Most recent"}</option>
          <option value="category">{lang==="es"?"↕ Por categoría":"↕ By category"}</option>
          <option value="title">{lang==="es"?"↕ Por título (A-Z)":"↕ By title (A-Z)"}</option>
          <option value="carrier">{lang==="es"?"↕ Por aseguradora":"↕ By carrier"}</option>
        </select>
      </div>

      {/* Resource Grid */}
      {filtered.length === 0 && (
        <div style={{ textAlign:"center", padding:"3rem 1rem", color:"var(--c-text3,#45688a)" }}>
          <div style={{ fontSize:"2.5rem", marginBottom:".5rem" }}>📚</div>
          <div style={{ fontWeight:700 }}>{tr.noResources||"No resources found"}</div>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:".75rem" }}>
        {filtered.map(res => (
          <div key={res.id} style={{ background:"var(--c-card,#0d1f2d)", border:"1px solid var(--c-border,#1a3a52)", borderRadius:12, overflow:"hidden", transition:"border-color .15s" }}
            onMouseEnter={e=>e.currentTarget.style.borderColor="var(--c-cyan,#00d4ff)"}
            onMouseLeave={e=>e.currentTarget.style.borderColor="var(--c-border,#1a3a52)"}>
            {/* Type banner */}
            <div style={{ background:typeColor(res.type), padding:".4rem .75rem", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span style={{ fontSize:".7rem", fontWeight:800, color:"#fff", textTransform:"uppercase", display:"flex", alignItems:"center", gap:4 }}>
                {typeIcon(res.type)} {res.type}
              </span>
              <span style={{ fontSize:".65rem", fontWeight:600, color:"rgba(255,255,255,.8)" }}>{res.carrier}</span>
            </div>
            {/* Body */}
            <div style={{ padding:".75rem" }}>
              <div style={{ fontWeight:700, fontSize:".85rem", color:"var(--c-white,#eef6ff)", marginBottom:".25rem", lineHeight:1.3 }}>{res.title}</div>
              {res.description && <div style={{ fontSize:".72rem", color:"var(--c-text3,#45688a)", marginBottom:".5rem", lineHeight:1.4, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" }}>{res.description}</div>}
              <div style={{ display:"flex", flexWrap:"wrap", gap:".25rem", marginBottom:".5rem" }}>
                <span style={{ fontSize:".6rem", padding:"2px 6px", borderRadius:4, background:"var(--c-hover,#132f42)", color:"var(--c-cyan,#00d4ff)", fontWeight:600 }}>{res.category}</span>
                {res.fileName && <span style={{ fontSize:".6rem", padding:"2px 6px", borderRadius:4, background:"var(--c-hover,#132f42)", color:"var(--c-text3,#45688a)" }}>{res.fileName} {fmtSize(res.fileSize)}</span>}
              </div>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", borderTop:"1px solid var(--c-border,#1a3a52)", paddingTop:".4rem", marginTop:".25rem" }}>
                <span style={{ fontSize:".6rem", color:"var(--c-text3,#45688a)" }}>
                  <Users size={10} style={{ marginRight:3 }}/> {sharedCount(res)}
                </span>
                <div style={{ display:"flex", gap:".3rem" }}>
                  {(res.fileUrl || res.fileData) && <button onClick={()=>openPreview(res)} title={tr.preview||"Preview"} style={{ background:"none", border:"none", color:"var(--c-cyan,#00d4ff)", cursor:"pointer", padding:2 }}><Eye size={14}/></button>}
                  {res.fileUrl && <button onClick={()=>copyResourceLink(res)} disabled={linkBusyId===res.id}
                    title={lang==="es"?"Copiar enlace para enviar por correo, mensaje o WhatsApp":"Copy a link to send by email, message or WhatsApp"}
                    style={{ background:"none", border:"none", color:copiedLinkId===res.id?"var(--c-green,#00ff88)":"var(--c-text3,#45688a)", cursor:linkBusyId===res.id?"wait":"pointer", padding:2 }}>
                    {copiedLinkId===res.id ? <Check size={14}/> : <Link2 size={14}/>}
                  </button>}
                  {canEdit && <button onClick={()=>handleEdit(res)} title={tr.editResource||"Edit"} style={{ background:"none", border:"none", color:"var(--c-text3,#45688a)", cursor:"pointer", padding:2 }}><Edit2 size={14}/></button>}
                  {canEdit && <button onClick={()=>handleDelete(res.id)} title={tr.deleteConfirm||"Delete"} style={{ background:"none", border:"none", color:"#ef4444", cursor:"pointer", padding:2 }}><Trash2 size={14}/></button>}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Resource Modal */}
      {showForm && (
        <Modal title={editingId ? (tr.editTitle||"Edit Resource") : (tr.addTitle||"New Resource")} onClose={()=>{setShowForm(false);setEditingId(null);setForm(emptyForm);}} wide>
          <div style={{ display:"flex", flexDirection:"column", gap:".75rem" }}>
            {/* Title */}
            <div>
              <label style={{ fontSize:".7rem", fontWeight:700, color:"var(--c-text3,#45688a)", display:"block", marginBottom:".2rem" }}>{tr.title_field||"Title"} *</label>
              <input value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="e.g. ACA Enrollment Guide" style={{ width:"100%", padding:".5rem .75rem", borderRadius:8, border:"1px solid var(--c-border,#1a3a52)", background:"var(--c-bg,#070d14)", color:"var(--c-white,#eef6ff)", fontSize:".85rem", boxSizing:"border-box" }}/>
            </div>
            {/* Description */}
            <div>
              <label style={{ fontSize:".7rem", fontWeight:700, color:"var(--c-text3,#45688a)", display:"block", marginBottom:".2rem" }}>{tr.description_field||"Description"}</label>
              <textarea value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} rows={2} style={{ width:"100%", padding:".5rem .75rem", borderRadius:8, border:"1px solid var(--c-border,#1a3a52)", background:"var(--c-bg,#070d14)", color:"var(--c-white,#eef6ff)", fontSize:".8rem", resize:"vertical", boxSizing:"border-box", fontFamily:"inherit" }}/>
            </div>
            {/* Type selector */}
            <div style={{ display:"flex", gap:".4rem", flexWrap:"wrap" }}>
              {["video","pdf","image","document"].map(typ=>(
                <button key={typ} onClick={()=>setForm(f=>({...f,type:typ,fileData:typ==="video"?"":f.fileData,fileUrl:typ!=="video"?"":f.fileUrl}))} style={{ padding:".35rem .7rem", borderRadius:8, border:form.type===typ?"2px solid var(--c-cyan,#00d4ff)":"1px solid var(--c-border,#1a3a52)", background:form.type===typ?"rgba(0,212,255,.1)":"transparent", color:form.type===typ?"var(--c-cyan,#00d4ff)":"var(--c-text3,#45688a)", fontSize:".75rem", fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
                  {typeIcon(typ)} {(tr[typ]||typ).charAt(0).toUpperCase()+(tr[typ]||typ).slice(1)}
                </button>
              ))}
            </div>
            {/* Insurance Type */}
            <div>
              <label style={{ fontSize:".7rem", fontWeight:700, color:"var(--c-text3,#45688a)", display:"block", marginBottom:".3rem" }}>{lang==="es"?"Tipo de Seguro":"Insurance Type"} *</label>
              <div style={{ display:"flex", gap:".3rem", flexWrap:"wrap" }}>
                {POLICY_TYPES.map(pt=>(
                  <button key={pt.id} onClick={()=>setForm(f=>({...f, policyType:pt.id, carrier:""}))} style={{ padding:".3rem .6rem", borderRadius:8, border:form.policyType===pt.id?"2px solid "+pt.color:"1px solid var(--c-border,#1a3a52)", background:form.policyType===pt.id?pt.color+"18":"transparent", color:form.policyType===pt.id?pt.color:"var(--c-text3,#45688a)", fontSize:".7rem", fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:3, transition:"all .15s" }}>
                    <span style={{fontSize:".8rem"}}>{pt.icon}</span> {lang==="es"?pt.labelEs:pt.label}
                  </button>
                ))}
              </div>
            </div>
            {/* Carrier (filtered by insurance type) + Category */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:".5rem" }}>
              <div>
                <label style={{ fontSize:".7rem", fontWeight:700, color:"var(--c-text3,#45688a)", display:"block", marginBottom:".2rem" }}>{tr.carrier||"Carrier"} <span style={{fontWeight:400,color:"var(--c-text3,#45688a)"}}>({lang==="es"?"opcional — déjalo vacío para entrenamiento del equipo":"optional — leave empty for team training"})</span></label>
                {filteredCarriers.length > 0 ? (
                  <>
                    <select value={form.carrier} onChange={e=>setForm(f=>({...f,carrier:e.target.value}))} style={{ width:"100%", padding:".5rem .75rem", borderRadius:8, border:"1px solid var(--c-border,#1a3a52)", background:"var(--c-bg,#070d14)", color:"var(--c-white,#eef6ff)", fontSize:".8rem", boxSizing:"border-box" }}>
                      <option value="">{lang==="es"?"— Ninguna / entrenamiento del equipo —":"— None / team training —"}</option>
                      {filteredCarriers.map(c=><option key={c} value={c}>{c}</option>)}
                    </select>
                    {form.policyType && <div style={{ marginTop:".25rem" }}>
                      <input value={filteredCarriers.includes(form.carrier)?"":form.carrier} onChange={e=>setForm(f=>({...f,carrier:e.target.value}))} placeholder={lang==="es"?"O escribir otra compañía...":"Or type another carrier..."} style={{ width:"100%", padding:".35rem .6rem", borderRadius:6, border:"1px solid var(--c-border,#1a3a52)", background:"var(--c-bg,#070d14)", color:"var(--c-white,#eef6ff)", fontSize:".72rem", boxSizing:"border-box", opacity:.8 }}/>
                    </div>}
                  </>
                ) : (
                  <input value={form.carrier} onChange={e=>setForm(f=>({...f,carrier:e.target.value}))} placeholder={lang==="es"?"Escribir nombre de compañía":"Type carrier name"} style={{ width:"100%", padding:".5rem .75rem", borderRadius:8, border:"1px solid var(--c-border,#1a3a52)", background:"var(--c-bg,#070d14)", color:"var(--c-white,#eef6ff)", fontSize:".8rem", boxSizing:"border-box" }}/>
                )}
              </div>
              <div>
                <label style={{ fontSize:".7rem", fontWeight:700, color:"var(--c-text3,#45688a)", display:"block", marginBottom:".2rem" }}>{tr.category||"Category"} *</label>
                <select value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))} style={{ width:"100%", padding:".5rem .75rem", borderRadius:8, border:"1px solid var(--c-border,#1a3a52)", background:"var(--c-bg,#070d14)", color:"var(--c-white,#eef6ff)", fontSize:".8rem", boxSizing:"border-box" }}>
                  <option value="">{tr.selectCategory||"Select category"}</option>
                  {allCategories.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            {/* File upload or Video URL */}
            {form.type === "video" ? (
              <div>
                <label style={{ fontSize:".7rem", fontWeight:700, color:"var(--c-text3,#45688a)", display:"block", marginBottom:".2rem" }}>{lang==="es"?"Video":"Video"} *</label>
                <input ref={videoFileRef} type="file" accept="video/*" style={{ display:"none" }} onChange={e=>handleVideoFile(e.target.files?.[0])}/>
                <button type="button" disabled={uploading} onClick={()=>videoFileRef.current?.click()} style={{ width:"100%", padding:".55rem .75rem", borderRadius:8, border:"1px dashed var(--c-cyan,#00d4ff)", background:"var(--c-bg,#070d14)", color:"var(--c-cyan,#00d4ff)", fontSize:".8rem", fontWeight:700, cursor:uploading?"wait":"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6, boxSizing:"border-box" }}>
                  <Upload size={15}/> {(form.fileName && isBlobFileUrl(form.fileUrl)) ? `✓ ${form.fileName}` : (lang==="es"?"Subir video desde mi computadora":"Upload video from my computer")}
                </button>
                {uploading && (
                  <div style={{ marginTop:".4rem" }}>
                    <div style={{ fontSize:".7rem", color:"var(--c-cyan,#00d4ff)", marginBottom:3 }}>{lang==="es"?"Subiendo…":"Uploading…"} {Math.round(uploadPct*100)}%</div>
                    <div style={{ height:6, background:"var(--c-surface2,#112538)", borderRadius:4, overflow:"hidden" }}><div style={{ height:"100%", width:`${Math.round(uploadPct*100)}%`, background:"var(--c-cyan,#00d4ff)", transition:"width .15s" }}/></div>
                  </div>
                )}
                <div style={{ textAlign:"center", fontSize:".68rem", color:"var(--c-text3,#45688a)", margin:".4rem 0" }}>{lang==="es"?"— o pega un link —":"— or paste a link —"}</div>
                <input value={isBlobFileUrl(form.fileUrl)?"":form.fileUrl} onChange={e=>setForm(f=>({...f,fileUrl:e.target.value,fileName:"",fileSize:0}))} placeholder="https://youtube.com/watch?v=... · https://vimeo.com/..." style={{ width:"100%", padding:".5rem .75rem", borderRadius:8, border:"1px solid var(--c-border,#1a3a52)", background:"var(--c-bg,#070d14)", color:"var(--c-white,#eef6ff)", fontSize:".8rem", boxSizing:"border-box" }}/>
              </div>
            ) : (
              <div>
                <label style={{ fontSize:".7rem", fontWeight:700, color:"var(--c-text3,#45688a)", display:"block", marginBottom:".2rem" }}>{tr.uploadFile||"Upload File"} {!editingId && "*"}</label>
                <div onClick={()=>fileInputRef.current?.click()} onDragOver={e=>e.preventDefault()} onDrop={e=>{e.preventDefault(); const f=e.dataTransfer.files?.[0]; if(f){const ev={target:{files:[f]}}; handleFileUpload(ev);}}}
                  style={{ border:"2px dashed var(--c-border,#1a3a52)", borderRadius:10, padding:"1.25rem", textAlign:"center", cursor:"pointer", background:"var(--c-bg,#070d14)", transition:"border-color .15s" }}
                  onMouseEnter={e=>e.currentTarget.style.borderColor="var(--c-cyan,#00d4ff)"}
                  onMouseLeave={e=>e.currentTarget.style.borderColor="var(--c-border,#1a3a52)"}>
                  {uploading ? (
                    <div>
                      <div style={{ fontSize:".7rem", color:"var(--c-cyan,#00d4ff)", marginBottom:3 }}>{lang==="es"?"Subiendo…":"Uploading…"} {Math.round(uploadPct*100)}%</div>
                      <div style={{ height:6, background:"var(--c-surface2,#112538)", borderRadius:4, overflow:"hidden" }}><div style={{ height:"100%", width:`${Math.round(uploadPct*100)}%`, background:"var(--c-cyan,#00d4ff)", transition:"width .15s" }}/></div>
                    </div>
                  ) : form.fileName ? (
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:".5rem" }}>
                      <span style={{ fontSize:"1.2rem" }}>{typeIcon(form.type)}</span>
                      <div>
                        <div style={{ fontWeight:700, fontSize:".8rem", color:"var(--c-white,#eef6ff)" }}>{form.fileName}</div>
                        <div style={{ fontSize:".65rem", color:"var(--c-text3,#45688a)" }}>{fmtSize(form.fileSize)}</div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Upload size={24} style={{ color:"var(--c-text3,#45688a)", marginBottom:".3rem" }}/>
                      <div style={{ fontSize:".75rem", color:"var(--c-text3,#45688a)" }}>{tr.dragDrop||"Drag & drop or click to upload"}</div>
                    </div>
                  )}
                  <input ref={fileInputRef} type="file" accept={form.type==="pdf"?".pdf":form.type==="image"?"image/*":".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"} onChange={handleFileUpload} style={{ display:"none" }}/>
                </div>
              </div>
            )}
            {/* Tags */}
            <div>
              <label style={{ fontSize:".7rem", fontWeight:700, color:"var(--c-text3,#45688a)", display:"block", marginBottom:".2rem" }}>{tr.tags||"Tags"} <span style={{fontWeight:400,fontSize:".65rem"}}>(comma separated)</span></label>
              <input value={form.tags} onChange={e=>setForm(f=>({...f,tags:e.target.value}))} placeholder="enrollment, aca, guide" style={{ width:"100%", padding:".5rem .75rem", borderRadius:8, border:"1px solid var(--c-border,#1a3a52)", background:"var(--c-bg,#070d14)", color:"var(--c-white,#eef6ff)", fontSize:".8rem", boxSizing:"border-box" }}/>
            </div>
            {/* Share with clients */}
            <div>
              <label style={{ fontSize:".7rem", fontWeight:700, color:"var(--c-text3,#45688a)", display:"block", marginBottom:".3rem" }}>{tr.shareWith||"Share with clients"}</label>
              <div style={{ marginBottom:".4rem" }}>
                <label style={{ display:"flex", alignItems:"center", gap:".4rem", cursor:"pointer", fontSize:".78rem", color:"var(--c-white,#eef6ff)" }}>
                  <input type="checkbox" checked={form.sharedWithAll} onChange={e=>setForm(f=>({...f,sharedWithAll:e.target.checked}))}/>
                  {tr.sharedWithAll||"Share with all clients"}
                </label>
              </div>
              {!form.sharedWithAll && (
                <div style={{ maxHeight:140, overflowY:"auto", border:"1px solid var(--c-border,#1a3a52)", borderRadius:8, padding:".4rem" }}>
                  {(clients||[]).map(c=>(
                    <label key={c.id} style={{ display:"flex", alignItems:"center", gap:".4rem", padding:".2rem .3rem", cursor:"pointer", fontSize:".75rem", color:"var(--c-white,#eef6ff)", borderRadius:4 }}
                      onMouseEnter={e=>e.currentTarget.style.background="var(--c-hover,#132f42)"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <input type="checkbox" checked={(form.sharedWithClients||[]).includes(c.id)} onChange={()=>toggleClientShare(c.id)}/>
                      {c.firstName} {c.lastName}
                    </label>
                  ))}
                </div>
              )}
            </div>
            {/* Actions */}
            <div style={{ display:"flex", gap:".5rem", justifyContent:"flex-end", marginTop:".25rem" }}>
              <button onClick={()=>{setShowForm(false);setEditingId(null);setForm(emptyForm);}} style={{ padding:".45rem 1rem", borderRadius:8, border:"1px solid var(--c-border,#1a3a52)", background:"transparent", color:"var(--c-text3,#45688a)", fontSize:".8rem", fontWeight:600, cursor:"pointer" }}>{tr.cancel||"Cancel"}</button>
              <button onClick={handleSave} style={{ padding:".45rem 1rem", borderRadius:8, border:"none", background:"var(--c-cyan,#00d4ff)", color:"#000", fontSize:".8rem", fontWeight:700, cursor:"pointer" }}>{tr.save||"Save Resource"}</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Generate Portal Link Modal */}
      {showPortalModal && (
        <Modal title={lang==="es"?"Generar Links del Portal":"Generate Portal Links"} onClose={()=>{setShowPortalModal(false);setPortalClientId("");setPortalCarrierFilter("");setPortalSelectedClients([]);setPortalGenerated([]);}} wide>
          <div style={{ display:"flex", flexDirection:"column", gap:".75rem" }}>
            {portalGenerated.length === 0 ? (<>
              {/* Step 1: Filter by carrier */}
              <div>
                <label style={{ fontSize:".7rem", fontWeight:700, color:"var(--c-text3,#45688a)", display:"block", marginBottom:".3rem" }}>{lang==="es"?"Filtrar por Compañía":"Filter by Carrier"}</label>
                <div style={{ display:"flex", gap:".3rem", flexWrap:"wrap", marginBottom:".3rem" }}>
                  <button onClick={()=>{setPortalCarrierFilter("");setPortalSelectedClients([]);}} style={{ padding:".3rem .65rem", borderRadius:8, border:!portalCarrierFilter?"2px solid var(--c-cyan,#00d4ff)":"1px solid var(--c-border,#1a3a52)", background:!portalCarrierFilter?"rgba(0,212,255,.1)":"transparent", color:!portalCarrierFilter?"var(--c-cyan,#00d4ff)":"var(--c-text3,#45688a)", fontSize:".72rem", fontWeight:700, cursor:"pointer" }}>
                    {lang==="es"?"Todos":"All"}
                  </button>
                  {allCarriers.map(c=>(
                    <button key={c} onClick={()=>{setPortalCarrierFilter(c);setPortalSelectedClients([]);}} style={{ padding:".3rem .65rem", borderRadius:8, border:portalCarrierFilter===c?"2px solid var(--c-cyan,#00d4ff)":"1px solid var(--c-border,#1a3a52)", background:portalCarrierFilter===c?"rgba(0,212,255,.1)":"transparent", color:portalCarrierFilter===c?"var(--c-cyan,#00d4ff)":"var(--c-text3,#45688a)", fontSize:".72rem", fontWeight:700, cursor:"pointer" }}>
                      {c}
                    </button>
                  ))}
                </div>
                {portalCarrierFilter && <div style={{ fontSize:".7rem", color:"var(--c-cyan,#00d4ff)", fontWeight:600 }}>
                  {portalFilteredClients.length} {lang==="es"?"clientes con":"clients with"} {portalCarrierFilter}
                </div>}
              </div>
              {/* Step 2: Select clients */}
              <div>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:".3rem" }}>
                  <label style={{ fontSize:".7rem", fontWeight:700, color:"var(--c-text3,#45688a)" }}>{lang==="es"?"Seleccionar Clientes":"Select Clients"} ({portalSelectedClients.length})</label>
                  {portalFilteredClients.length > 0 && <button onClick={selectAllPortalClients} style={{ background:"none", border:"none", color:"var(--c-cyan,#00d4ff)", fontSize:".7rem", fontWeight:700, cursor:"pointer", padding:0 }}>
                    {portalFilteredClients.every(c=>portalSelectedClients.includes(c.id)) ? (lang==="es"?"Deseleccionar todos":"Deselect all") : (lang==="es"?"Seleccionar todos":"Select all")}
                  </button>}
                </div>
                <div style={{ maxHeight:220, overflowY:"auto", border:"1px solid var(--c-border,#1a3a52)", borderRadius:8, padding:".3rem" }}>
                  {portalFilteredClients.length === 0 ? (
                    <div style={{ textAlign:"center", padding:"1rem", fontSize:".75rem", color:"var(--c-text3,#45688a)" }}>
                      {lang==="es"?"No hay clientes con esta compañía":"No clients with this carrier"}
                    </div>
                  ) : portalFilteredClients.map(c => {
                    const clientPolicies = (policies||[]).filter(p=>p.clientId===c.id && (!portalCarrierFilter || p.carrier===portalCarrierFilter));
                    const isSelected = portalSelectedClients.includes(c.id);
                    return (
                      <label key={c.id} style={{ display:"flex", alignItems:"center", gap:".5rem", padding:".35rem .5rem", cursor:"pointer", borderRadius:6, fontSize:".78rem", color:"var(--c-white,#eef6ff)", background:isSelected?"rgba(0,212,255,.08)":"transparent", transition:"background .1s" }}
                        onMouseEnter={e=>{if(!isSelected)e.currentTarget.style.background="var(--c-hover,#132f42)";}}
                        onMouseLeave={e=>{e.currentTarget.style.background=isSelected?"rgba(0,212,255,.08)":"transparent";}}>
                        <input type="checkbox" checked={isSelected} onChange={()=>togglePortalClient(c.id)} style={{accentColor:"var(--c-cyan,#00d4ff)"}}/>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:600 }}>{c.firstName} {c.lastName}</div>
                          <div style={{ fontSize:".65rem", color:"var(--c-text3,#45688a)", display:"flex", gap:".5rem", flexWrap:"wrap" }}>
                            {c.email && <span>{c.email}</span>}
                            {clientPolicies.length > 0 && <span style={{ color:"var(--c-cyan,#00d4ff)" }}>
                              {clientPolicies.map(p=>{const pt=getPolicyType(p.type);return pt.icon;}).join(" ")} {portalCarrierFilter||clientPolicies.map(p=>p.carrier).filter((v,i,a)=>a.indexOf(v)===i).join(", ")}
                            </span>}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
              {/* Actions */}
              <div style={{ display:"flex", gap:".5rem", justifyContent:"flex-end", marginTop:".25rem" }}>
                <button onClick={()=>{setShowPortalModal(false);setPortalCarrierFilter("");setPortalSelectedClients([]);setPortalGenerated([]);}} style={{ padding:".45rem 1rem", borderRadius:8, border:"1px solid var(--c-border,#1a3a52)", background:"transparent", color:"var(--c-text3,#45688a)", fontSize:".8rem", fontWeight:600, cursor:"pointer" }}>{tr.cancel||"Cancel"}</button>
                <button onClick={generatePortalLinks} disabled={portalSelectedClients.length===0 || portalBusy} style={{ padding:".45rem 1rem", borderRadius:8, border:"none", background:(portalSelectedClients.length>0 && !portalBusy)?"var(--c-green,#00ff88)":"#333", color:(portalSelectedClients.length>0 && !portalBusy)?"#000":"#666", fontSize:".8rem", fontWeight:700, cursor:portalBusy?"wait":(portalSelectedClients.length>0?"pointer":"not-allowed"), display:"flex", alignItems:"center", gap:4 }}>
                  <Link2 size={13}/> {portalBusy
                    ? (lang==="es"?"Generando…":"Generating…")
                    : (lang==="es"?`Generar ${portalSelectedClients.length} Link${portalSelectedClients.length>1?"s":""}`:`Generate ${portalSelectedClients.length} Link${portalSelectedClients.length>1?"s":""}`)}
                </button>
              </div>
            </>) : (<>
              {/* Step 3: Results */}
              <div style={{ fontSize:".8rem", color:"var(--c-green,#00ff88)", fontWeight:700, display:"flex", alignItems:"center", gap:".4rem" }}>
                <CheckCircle size={16}/> {lang==="es"?`${portalGenerated.length} links generados`:`${portalGenerated.length} links generated`}
              </div>
              <div style={{ maxHeight:280, overflowY:"auto", display:"flex", flexDirection:"column", gap:".4rem" }}>
                {portalGenerated.map(g=>(
                  <div key={g.clientId} style={{ background:"var(--c-hover,#132f42)", borderRadius:8, padding:".5rem .65rem", display:"flex", alignItems:"center", justifyContent:"space-between", gap:".5rem" }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:700, fontSize:".78rem", color:"var(--c-white,#eef6ff)" }}>{g.name}</div>
                      <div style={{ fontSize:".62rem", color:"var(--c-text3,#45688a)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{g.email && <span>{g.email} · </span>}{g.link}</div>
                      {revocados.includes(g.token)
                        ? <div style={{ fontSize:".62rem", color:"#f87171", fontWeight:700 }}>{lang==="es"?"Anulado — ya no abre":"Revoked — no longer opens"}</div>
                        : (g.expiresAt && <div style={{ fontSize:".62rem", color:"var(--c-text3,#45688a)" }}>{vigenciaTexto(g.expiresAt)}</div>)}
                    </div>
                    {!revocados.includes(g.token) && (<>
                    <button onClick={()=>copySinglePortalLink(g.link,g.clientId)} style={{ background:copiedLink===g.clientId?"var(--c-green,#00ff88)":"var(--c-cyan,#00d4ff)", color:"#000", border:"none", borderRadius:6, padding:".3rem .55rem", fontSize:".68rem", fontWeight:700, cursor:"pointer", whiteSpace:"nowrap", display:"flex", alignItems:"center", gap:3 }}>
                      {copiedLink===g.clientId ? <><Check size={11}/> {lang==="es"?"Copiado":"Copied"}</> : <><Copy size={11}/> {lang==="es"?"Copiar":"Copy"}</>}
                    </button>
                    {g.token && (
                      <button onClick={()=>revocarPortal(g)} title={lang==="es"?"Anular este enlace":"Revoke this link"} style={{ background:"transparent", color:"#f87171", border:"1px solid var(--c-border,#1a3a52)", borderRadius:6, padding:".3rem .5rem", fontSize:".68rem", fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
                        {lang==="es"?"Anular":"Revoke"}
                      </button>
                    )}
                    </>)}
                  </div>
                ))}
              </div>
              {/* Bulk actions */}
              <div style={{ display:"flex", gap:".5rem", justifyContent:"flex-end", borderTop:"1px solid var(--c-border,#1a3a52)", paddingTop:".5rem" }}>
                <button onClick={()=>{setPortalGenerated([]);setPortalSelectedClients([]);}} style={{ padding:".4rem .85rem", borderRadius:8, border:"1px solid var(--c-border,#1a3a52)", background:"transparent", color:"var(--c-text3,#45688a)", fontSize:".78rem", fontWeight:600, cursor:"pointer" }}>
                  {lang==="es"?"Generar más":"Generate more"}
                </button>
                <button onClick={copyAllPortalLinks} style={{ padding:".4rem .85rem", borderRadius:8, border:"none", background:copiedLink==="all"?"var(--c-green,#00ff88)":"var(--c-cyan,#00d4ff)", color:"#000", fontSize:".78rem", fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
                  {copiedLink==="all" ? <><Check size={12}/> {lang==="es"?"Todos copiados":"All copied"}</> : <><Copy size={12}/> {lang==="es"?"Copiar todos":"Copy all"}</>}
                </button>
                <button onClick={()=>{setShowPortalModal(false);setPortalCarrierFilter("");setPortalSelectedClients([]);setPortalGenerated([]);}} style={{ padding:".4rem .85rem", borderRadius:8, border:"none", background:"var(--c-green,#00ff88)", color:"#000", fontSize:".78rem", fontWeight:700, cursor:"pointer" }}>
                  {lang==="es"?"Listo":"Done"}
                </button>
              </div>
            </>)}
          </div>
        </Modal>
      )}

      {/* Preview Modal */}
      {previewRes && (
        <Modal title={previewRes.title} onClose={()=>setPreviewRes(null)} xl>
          <div style={{ textAlign:"center" }}>
            {previewRes.type === "video" && getYouTubeId(previewRes.fileUrl) && (
              <iframe width="100%" height="360" src={`https://www.youtube.com/embed/${getYouTubeId(previewRes.fileUrl)}`} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ borderRadius:10 }}/>
            )}
            {previewRes.type === "video" && getVimeoId(previewRes.fileUrl) && (
              <iframe width="100%" height="360" src={`https://player.vimeo.com/video/${getVimeoId(previewRes.fileUrl)}`} frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen style={{ borderRadius:10 }}/>
            )}
            {previewRes.type === "video" && !getYouTubeId(previewRes.fileUrl) && !getVimeoId(previewRes.fileUrl) && (
              (isBlobFileUrl(previewRes.fileUrl) || previewRes.fileName) ? (
                <VisorDeVideo src={previewRes.fileUrl} lang={lang} nombre={previewRes.fileName} style={{ maxWidth:"100%", maxHeight:500, borderRadius:10 }}/>
              ) : (
                <div style={{ padding:"2rem", color:"var(--c-text3,#45688a)" }}>
                  <a href={previewRes.fileUrl} target="_blank" rel="noopener noreferrer" style={{ color:"var(--c-cyan,#00d4ff)" }}>{previewRes.fileUrl}</a>
                </div>
              )
            )}
            {previewRes.type === "image" && (previewRes.fileUrl || previewRes.fileData) && (
              <img src={previewRes.fileUrl || previewRes.fileData} alt={previewRes.title} style={{ maxWidth:"100%", maxHeight:500, borderRadius:10 }}/>
            )}
            {previewRes.type === "pdf" && (previewRes.fileUrl || previewRes.fileData) && (
              <iframe src={previewRes.fileUrl || previewRes.fileData} width="100%" height="500" style={{ borderRadius:10, border:"none" }}/>
            )}
            {previewRes.type === "document" && (previewRes.fileUrl || previewRes.fileData) && (
              <div style={{ padding:"1.5rem", color:"var(--c-text3,#45688a)" }}>
                <a href={previewRes.fileUrl || previewRes.fileData} download={previewRes.fileName} target="_blank" rel="noopener noreferrer" style={{ color:"var(--c-cyan,#00d4ff)", display:"flex", alignItems:"center", justifyContent:"center", gap:".4rem", fontSize:".9rem", fontWeight:600 }}>
                  <Download size={16}/> {lang==="es"?"Descargar":"Download"} {previewRes.fileName}
                </a>
              </div>
            )}
            <div style={{ marginTop:".75rem", fontSize:".75rem", color:"var(--c-text3,#45688a)", lineHeight:1.5 }}>
              <div><strong>{tr.carrier||"Carrier"}:</strong> {previewRes.carrier} &bull; <strong>{tr.category||"Category"}:</strong> {previewRes.category}</div>
              {previewRes.description && <div style={{ marginTop:".3rem" }}>{previewRes.description}</div>}
            </div>
          </div>
        </Modal>
      )}

      {/* Copied feedback */}
      {copiedLink && (
        <div style={{ position:"fixed", bottom:20, right:20, background:"var(--c-green,#00ff88)", color:"#000", padding:".6rem 1.2rem", borderRadius:10, fontWeight:700, fontSize:".8rem", zIndex:9999, boxShadow:"0 4px 20px rgba(0,255,136,.3)", display:"flex", alignItems:"center", gap:".4rem" }}>
          <CheckCircle size={16}/> {tr.portalLinkCopied||"Portal link copied!"}
        </div>
      )}
    </div>
  );
}
